/**
 * API Client for Dashboard Metrics and Logs
 *
 * Handles communication with the dashboard backend API.
 * Provides functions to fetch CloudWatch metrics and logs.
 */

import type {
  MetricsResponse,
  LogsResponse,
  CostsResponse,
  TimeRange,
} from '../types/dashboard';
import {
  createTimeoutSignal,
  debugLog,
  getBaseUrl,
  handleHttpError,
  handleNetworkError,
} from './api-utils';

/**
 * Fetch dashboard metrics for the specified time range
 *
 * @param timeRange - Time range for metrics (1d, 3d, 7d, or 30d)
 * @param authHeader - Authorization header object from auth context
 * @param signal - Optional AbortSignal for request cancellation
 * @returns Promise resolving to MetricsResponse
 * @throws Error on network errors, auth errors, or HTTP errors
 */
export async function fetchDashboardMetrics(
  timeRange: TimeRange,
  authHeader: Record<string, string>,
  signal?: AbortSignal,
): Promise<MetricsResponse> {
  const endpoint = `${getBaseUrl()}/dashboard/metrics?timeRange=${timeRange}`;

  debugLog('Fetching dashboard metrics:', { endpoint, timeRange });

  try {
    const response = await fetch(endpoint, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...authHeader,
      },
      signal: createTimeoutSignal(signal),
    });

    await handleHttpError(response);

    const data = await response.json();

    debugLog('Received dashboard metrics:', {
      timeRange: data.timeRange,
      totalQueries: data.summary?.totalQueries,
    });

    return data as MetricsResponse;
  } catch (error) {
    handleNetworkError(error);
  }
}

/**
 * Fetch dashboard logs for the specified log group
 *
 * @param logGroup - CloudWatch log group name
 * @param authHeader - Authorization header object from auth context
 * @param options - Optional parameters for pagination, filtering, and time range
 * @param signal - Optional AbortSignal for request cancellation
 * @returns Promise resolving to LogsResponse
 * @throws Error on network errors, auth errors, or HTTP errors
 */
export async function fetchDashboardLogs(
  logGroup: string,
  authHeader: Record<string, string>,
  options?: {
    limit?: number;
    offset?: number;
    filter?: string;
    startTime?: number;
    timeRange?: TimeRange;
  },
  signal?: AbortSignal,
): Promise<LogsResponse> {
  const params = new URLSearchParams({
    logGroup,
  });

  if (options?.limit) {
    params.set('limit', options.limit.toString());
  }
  if (options?.offset !== undefined) {
    params.set('offset', options.offset.toString());
  }
  if (options?.filter) {
    params.set('filter', options.filter);
  }
  if (options?.startTime) {
    params.set('startTime', options.startTime.toString());
  }
  if (options?.timeRange) {
    params.set('timeRange', options.timeRange);
  }

  const endpoint = `${getBaseUrl()}/dashboard/logs?${params.toString()}`;

  debugLog('Fetching dashboard logs:', {
    endpoint,
    logGroup,
    limit: options?.limit,
    offset: options?.offset,
  });

  try {
    const response = await fetch(endpoint, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...authHeader,
      },
      signal: createTimeoutSignal(signal),
    });

    await handleHttpError(response);

    const data = await response.json();

    debugLog('Received dashboard logs:', {
      logGroup: data.logGroup,
      eventCount: data.events?.length,
      hasMore: data.hasMore,
    });

    return data as LogsResponse;
  } catch (error) {
    handleNetworkError(error);
  }
}

/**
 * Fetch AWS cost data for the specified time range
 *
 * @param timeRange - Time range for costs (1d, 3d, 7d, or 30d)
 * @param authHeader - Authorization header object from auth context
 * @param signal - Optional AbortSignal for request cancellation
 * @returns Promise resolving to CostsResponse
 * @throws Error on network errors, auth errors, or HTTP errors
 */
export async function fetchDashboardCosts(
  timeRange: TimeRange,
  authHeader: Record<string, string>,
  signal?: AbortSignal,
): Promise<CostsResponse> {
  const endpoint = `${getBaseUrl()}/dashboard/costs?timeRange=${timeRange}`;

  debugLog('Fetching dashboard costs:', { endpoint, timeRange });

  try {
    const response = await fetch(endpoint, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...authHeader,
      },
      signal: createTimeoutSignal(signal),
    });

    await handleHttpError(response);

    const data = await response.json();

    debugLog('Received dashboard costs:', {
      timeRange: data.timeRange,
      totalCost: data.summary?.totalCost,
    });

    return data as CostsResponse;
  } catch (error) {
    handleNetworkError(error);
  }
}
